// ============================================================
// HipWear — ResultsPanel
// Analysis results display and interactive table
// ============================================================

import type { AnalysisResults, AnalysisRunResult, Geodesic, MultiModeComparisonResults, SphericityResult } from '../types';

export class ResultsPanel {
  private container: HTMLElement;
  private sidebar: HTMLElement;
  private closeBtn: HTMLElement;
  private onGeodesicSelect?: (angle: number) => void;
  private yearsInVivo: number = 0;

  /** Update the implantation duration used for wear rate computation. */
  setYearsInVivo(years: number): void {
    this.yearsInVivo = years;
  }

  constructor() {
    this.container = document.getElementById('results-container')!;
    this.sidebar = document.getElementById('sidebar-right')!;
    this.closeBtn = document.getElementById('btn-close-results')!;

    this.closeBtn.addEventListener('click', () => this.hide());
  }

  /**
   * Set callback for geodesic selection in the table.
   */
  setGeodesicSelectHandler(handler: (angle: number) => void): void {
    this.onGeodesicSelect = handler;
  }

  /**
   * Show results panel with full analysis data.
   */
  show(results: AnalysisRunResult): void {
    this.sidebar.classList.remove('hidden');
    this.container.innerHTML = '';

    if (results.analysisMode === 'compare-all-modes') {
      this.addCompareTopSummary(results);
      if (results.sphericity) this.addSphericitySection(results.sphericity);
      if (results.twoSphereAuto) this.renderSingleResult(results.twoSphereAuto, 'Two-Sphere Auto', 'ts');
      this.renderSingleResult(results.sphereBestfit, 'Sphere BestFit (legacy)', 'sbf');
      this.renderSingleResult(results.doubleSphereMetrics, 'Double Sphere Metrics (legacy)', 'dsm');
      window.dispatchEvent(new Event('resize'));
      return;
    }

    this.renderSingleResult(results);

    // Trigger layout resize
    window.dispatchEvent(new Event('resize'));
  }

  private renderSingleResult(results: AnalysisResults, titlePrefix?: string, modeKey?: 'sbf' | 'dsm' | 'ts'): void {
    if (titlePrefix && modeKey) {
      this.addModeDetailHeader(titlePrefix, modeKey);
    }

    if (results.analysisMode === 'manual-geodesic') {
      this.addManualGeodesicComparisonSummary(results);
    }

    // Legacy modes: not validated with known-wear phantoms
    if (results.analysisMode === 'sphere-bestfit' || results.analysisMode === 'double-sphere-metrics') {
      const warn = this.createSection('Legacy mode — not validated');
      this.addMetric(warn, 'Warning', results.analysisMode === 'sphere-bestfit'
        ? 'Automatic reference sphere: underestimates the volume when wear is extensive. Use Two-Sphere Auto or Manual Geodesic.'
        : 'Free-radius spheres, ignores the rim inclination: underestimates the volume and misses wear near the pole. Use Two-Sphere Auto or Manual Geodesic.',
        undefined, 'warning');
      this.container.appendChild(warn);
    }

    // KPI cards at the top for quick overview
    this.addKpiCards(results);

    // Full summary section
    this.addSummarySection(results);

    // Sphericity is a property of the surface: shown once (in compare mode it is shown above the modes)
    if (!modeKey && results.sphericity) this.addSphericitySection(results.sphericity);

    // Sphere fit section
    this.addSphereFitSection(results);

    if (results.analysisMode === 'sphere-bestfit' || results.analysisMode === 'manual-geodesic' || results.analysisMode === 'two-sphere-auto') {
      // --- Sphere BestFit / Manual Geodesic / Two-sphere mode sections ---
      if (results.twoSphere) {
        this.addTwoSphereSection(results);
      }
      if (results.commercialSphere) {
        this.addCommercialSphereSection(results);
      }
      if (results.wearClassification) {
        this.addWearClassificationSection(results);
      }
      if (results.zoneSpheres) {
        this.addZoneSpheresSection(results);
      }
      if (results.wearVolumeResult) {
        this.addWearVolumeSection(results);
      }
      if (results.wearPlane) {
        this.addWearPlaneSection(results);
      }
    } else if (results.analysisMode === 'double-sphere-metrics') {
      this.addDoubleSphereSection(results);
      if (results.wearVolumeResult) {
        this.addWearVolumeSection(results);
      }
    } else {
      // --- Pure Geodesic mode sections ---
      if (results.ellipsoidFit) {
        this.addShapeSection(results);
      }
      this.addWearSection(results);
      this.addVolumeSection(results);
      if (results.wearVector) {
        this.addWearVectorSection(results);
      }
    }

    // Geodesic table section
    this.addGeodesicTable(results.geodesics);
  }

  hide(): void {
    this.sidebar.classList.add('hidden');
    window.dispatchEvent(new Event('resize'));
  }

  private addSphericitySection(s: SphericityResult): void {
    const section = this.createSection('Sphericity');
    this.addMetric(section, 'Sphericity', s.sphericityPercent.toFixed(2), '%', undefined, true);
    this.addMetric(section, 'Form deviation (peak-to-valley)', s.sphericityUm.toFixed(1), 'μm');
    this.addMetric(section, 'Form error RMS', s.formRmsUm.toFixed(1), 'μm');
    this.addMetric(section, 'Least-squares sphere radius', s.radiusMm.toFixed(3), 'mm');
    this.addMetric(section, 'Raw point-wise P–V / RMS (incl. scanner noise)',
      `${s.rawPeakToValleyUm.toFixed(0)} / ${s.pointRmsUm.toFixed(1)}`, 'μm');
    this.addMetric(section, 'Method',
      `radial deviation from the least-squares sphere of the trimmed bearing surface (excluding ${s.edgeBandMm} mm along its borders), averaged over ${s.cellSizeMm} mm cells ` +
      `(${s.cellCount} cells, ${s.pointCount} points), P0.5–P99.5; sphericity % = 100·(1 − P–V / R) — same value for every analysis mode`);
    this.container.appendChild(section);
  }

  private addSummarySection(results: AnalysisResults): void {
    const section = this.createSection('Summary');

    const modeLabel =
      results.analysisMode === 'sphere-bestfit'
        ? 'Sphere BestFit (legacy)'
        : results.analysisMode === 'manual-geodesic'
          ? 'Manual Geodesic'
          : results.analysisMode === 'two-sphere-auto'
            ? 'Two-Sphere Auto'
          : results.analysisMode === 'double-sphere-metrics'
            ? 'Double Sphere Metrics (legacy)'
            : 'Pure Geodesic';
    this.addMetric(section, 'Mode', modeLabel);
    this.addMetric(section, 'Vertices', results.vertexCount.toLocaleString());
    this.addMetric(section, 'Faces', results.faceCount.toLocaleString());
    this.addMetric(section, 'Geodesics', results.geodesicCount.toString());
    this.addMetric(section, 'Processing Time',
      `${(results.processingTimeMs / 1000).toFixed(1)}`, 's');

    this.container.appendChild(section);
  }

  // -------------------------------------------------------
  // Compare-all: large summary table at the very top
  // -------------------------------------------------------

  private addCompareTopSummary(results: MultiModeComparisonResults): void {
    const section = document.createElement('div');
    section.className = 'results-section fade-in';

    const sbf = results.sphereBestfit;
    const dsm = results.doubleSphereMetrics;
    const ts = results.twoSphereAuto;
    const tsDepth: number | null = ts?.wearPlane?.maxWearDepth ?? null;
    const tsLinear: number | null = ts?.twoSphere ? ts.twoSphere.linearWearMm * 1000 : null;
    const tsVol: number | null = ts?.wearVolumeResult?.wearVolume ?? null;
    const warnTs = ts?.zoneSpheres?.linearWearUnreliable ?? false;
    const tsCol = (html: string) => (ts ? html : '');

    // Max wear depth (μm)
    const sbfDepth: number | null = sbf.wearPlane?.maxWearDepth ?? null;
    const dsmDepth: number | null = dsm.wearPlane?.maxWearDepth ?? null;

    // Linear wear = center-distance shift (μm)
    const sbfLinear: number | null = sbf.zoneSpheres
      ? sbf.zoneSpheres.wornSphere.center.distanceTo(sbf.zoneSpheres.unwornSphere.center) * 1000
      : null;
    const dsmLinear: number | null =
      dsm.doubleSphereMetrics?.bestCell != null
        ? dsm.doubleSphereMetrics.bestCell.centerDistanceMean * 1000
        : null;

    // Volumetric wear (mm³)
    const sbfVol: number | null = sbf.wearVolumeResult?.wearVolume ?? null;
    const dsmVol: number | null = dsm.wearVolumeResult?.wearVolume ?? null;

    // Worn % (sphere bestfit only)
    const sbfWornPct: number | null = sbf.wearClassification?.wornPercent ?? null;

    const warnSbf = sbf.zoneSpheres?.linearWearUnreliable ?? false;
    const warnDsm = !dsm.doubleSphereMetrics?.bestCell;

    const cell = (val: number | null, dec: number, unit: string): string => {
      if (val === null) return '<td class="col-val"><span class="cmp-na">—</span></td>';
      const cls = val > 50 ? 'danger' : val > 20 ? 'warning' : 'success';
      return `<td class="col-val"><span class="cmp-num ${cls}">${val.toFixed(dec)}</span><span class="cmp-unit">${unit}</span></td>`;
    };

    const tableHtml = (rows: string[]) => `
      <table class="wear-compare-table">
        <thead>
          <tr>
            <th class="col-metric">Metric</th>
            <th class="col-sbf">Sphere BestFit<br><small>(legacy)</small></th>
            <th class="col-dsm">Double Sphere<br><small>(legacy)</small></th>
            ${ts ? '<th class="col-ts">Two-Sphere Auto</th>' : ''}
          </tr>
        </thead>
        <tbody>${rows.join('')}</tbody>
      </table>`;

    const rows: string[] = [];

    // Max Wear Depth row
    rows.push(`<tr>
      <td class="col-metric">Maximum wear depth</td>
      ${cell(sbfDepth, 1, 'μm')}
      ${cell(dsmDepth, 1, 'μm')}
      ${tsCol(cell(tsDepth, 1, 'μm'))}
    </tr>`);

    // Linear Wear row
    rows.push(`<tr>
      <td class="col-metric">Linear wear${warnSbf || warnDsm || warnTs ? ' ⚠' : ''}</td>
      ${cell(sbfLinear, 1, 'μm')}
      ${cell(dsmLinear, 1, 'μm')}
      ${tsCol(cell(tsLinear, 1, 'μm'))}
    </tr>`);

    // Volumetric Wear row
    rows.push(`<tr>
      <td class="col-metric">Wear volume</td>
      ${cell(sbfVol, 4, 'mm³')}
      ${cell(dsmVol, 4, 'mm³')}
      ${tsCol(cell(tsVol, 4, 'mm³'))}
    </tr>`);

    // Worn % row (only SBF has it)
    if (sbfWornPct !== null) {
      rows.push(`<tr>
        <td class="col-metric">Worn surface %</td>
        <td class="col-val">
          <span class="cmp-num ${sbfWornPct > 10 ? 'danger' : sbfWornPct > 2 ? 'warning' : 'success'}">${sbfWornPct.toFixed(1)}</span>
          <span class="cmp-unit">%</span>
        </td>
        <td class="col-val"><span class="cmp-na">—</span></td>
        ${tsCol(cell(ts?.wearClassification?.wornPercent ?? null, 1, '%'))}
      </tr>`);
    }

    const titleDiv = document.createElement('div');
    titleDiv.className = 'compare-section-title';
    titleDiv.innerHTML = `<span class="icon">📊</span> Wear Metrics — Compared Modes`;
    section.appendChild(titleDiv);
    section.insertAdjacentHTML('beforeend', tableHtml(rows));

    // --- Wear Rates table (only if years in vivo set) ---
    if (this.yearsInVivo > 0) {
      const y = this.yearsInVivo;
      const rateCell = (val: number | null, dec: number, unit: string): string => {
        if (val === null) return '<td class="col-val"><span class="cmp-na">—</span></td>';
        return `<td class="col-val"><span class="cmp-num" style="color:#389aed">${(val / y).toFixed(dec)}</span><span class="cmp-unit">${unit}</span></td>`;
      };

      const rateRows: string[] = [];
      rateRows.push(`<tr>
        <td class="col-metric">Linear wear rate</td>
        ${rateCell(sbfLinear != null ? sbfLinear / 1000 : null, 4, 'mm/yr')}
        ${rateCell(dsmLinear != null ? dsmLinear / 1000 : null, 4, 'mm/yr')}
        ${tsCol(rateCell(tsLinear != null ? tsLinear / 1000 : null, 4, 'mm/yr'))}
      </tr>`);
      rateRows.push(`<tr>
        <td class="col-metric">Volumetric wear rate</td>
        ${rateCell(sbfVol, 4, 'mm³/yr')}
        ${rateCell(dsmVol, 4, 'mm³/yr')}
        ${tsCol(rateCell(tsVol, 4, 'mm³/yr'))}
      </tr>`);

      const rateTitle = document.createElement('div');
      rateTitle.className = 'compare-section-title';
      rateTitle.style.marginTop = '14px';
      rateTitle.innerHTML = `<span class="icon">⏱</span> Wear Rates — ${y} yr${y !== 1 ? 's' : ''} in vivo`;
      section.appendChild(rateTitle);
      section.insertAdjacentHTML('beforeend', tableHtml(rateRows));
    }

    // processing time
    const ptRow = document.createElement('div');
    ptRow.className = 'metric-row';
    ptRow.style.marginTop = '10px';
    ptRow.innerHTML = `<span class="metric-label">Processing time</span><span class="metric-value">${(results.processingTimeMs / 1000).toFixed(1)}<span class="metric-unit">s</span></span>`;
    section.appendChild(ptRow);

    this.container.appendChild(section);
  }

  private addManualGeodesicComparisonSummary(results: AnalysisResults): void {
    const section = document.createElement('div');
    section.className = 'results-section fade-in';

    const linearWear = results.zoneSpheres
      ? results.zoneSpheres.wornSphere.center.distanceTo(results.zoneSpheres.unwornSphere.center) * 1000
      : null;
    const volumetricWear = results.wearVolumeResult?.wearVolume ?? null;
    const maxWearDepth = results.wearPlane?.maxWearDepth ?? null;
    const wornPct = results.wearClassification?.wornPercent ?? null;
    const sphericity = results.sphericity?.sphericityPercent ?? results.ellipsoidFit?.sphericityPercent ?? null;

    const cell = (val: number | null, dec: number, unit: string): string => {
      if (val === null) return '<td class="col-val"><span class="cmp-na">—</span></td>';
      const cls = val > 50 ? 'danger' : val > 20 ? 'warning' : 'success';
      return `<td class="col-val"><span class="cmp-num ${cls}">${val.toFixed(dec)}</span><span class="cmp-unit">${unit}</span></td>`;
    };

    const tableRows: string[] = [];
    tableRows.push(`<tr><td class="col-metric">Wear volume</td>${cell(volumetricWear, 4, 'mm³')}</tr>`);
    tableRows.push(`<tr><td class="col-metric">Linear wear</td>${cell(linearWear, 1, 'μm')}</tr>`);
    tableRows.push(`<tr><td class="col-metric">Max. wear depth</td>${cell(maxWearDepth, 1, 'μm')}</tr>`);
    tableRows.push(`<tr><td class="col-metric">Worn surface %</td>${cell(wornPct, 1, '%')}</tr>`);
    tableRows.push(`<tr><td class="col-metric">Sphericity</td>${cell(sphericity, 2, '%')}</tr>`);

    const titleDiv = document.createElement('div');
    titleDiv.className = 'compare-section-title';
    titleDiv.innerHTML = `<span class="icon">📊</span> Manual Geodesic — Comparative Metrics`;
    section.appendChild(titleDiv);
    section.insertAdjacentHTML('beforeend', `
      <table class="wear-compare-table">
        <thead>
          <tr>
            <th class="col-metric">Metric</th>
            <th class="col-sbf">Manual Geodesic</th>
          </tr>
        </thead>
        <tbody>${tableRows.join('')}</tbody>
      </table>
    `);

    if (this.yearsInVivo > 0) {
      const y = this.yearsInVivo;
      const rateCell = (val: number | null, dec: number, unit: string): string => {
        if (val === null) return '<td class="col-val"><span class="cmp-na">—</span></td>';
        return `<td class="col-val"><span class="cmp-num" style="color:#389aed">${(val / y).toFixed(dec)}</span><span class="cmp-unit">${unit}</span></td>`;
      };

      const rateRows: string[] = [];
      rateRows.push(`<tr><td class="col-metric">Linear wear rate</td>${rateCell(linearWear != null ? linearWear / 1000 : null, 4, 'mm/yr')}</tr>`);
      rateRows.push(`<tr><td class="col-metric">Volumetric wear rate</td>${rateCell(volumetricWear, 4, 'mm³/yr')}</tr>`);

      const rateTitle = document.createElement('div');
      rateTitle.className = 'compare-section-title';
      rateTitle.style.marginTop = '14px';
      rateTitle.innerHTML = `<span class="icon">⏱</span> Wear Rates — ${y} yr${y !== 1 ? 's' : ''} in vivo`;
      section.appendChild(rateTitle);
      section.insertAdjacentHTML('beforeend', `
        <table class="wear-compare-table">
          <thead>
            <tr>
              <th class="col-metric">Metric</th>
              <th class="col-sbf">Manual Geodesic</th>
            </tr>
          </thead>
          <tbody>${rateRows.join('')}</tbody>
        </table>
      `);
    }

    this.container.appendChild(section);
  }

  // -------------------------------------------------------
  // Mode detail header (used in compare-all sub-sections)
  // -------------------------------------------------------

  private addModeDetailHeader(modeName: string, modeKey: 'sbf' | 'dsm' | 'ts'): void {
    const wrap = document.createElement('div');
    wrap.style.cssText = 'padding: 14px 16px 0; display:flex; align-items:center; gap:8px;';
    wrap.innerHTML = `
      <span class="mode-badge ${modeKey}">${modeName}</span>
      <hr style="flex:1; border:none; border-top:1px solid var(--border-subtle); margin:0;">
    `;
    this.container.appendChild(wrap);
  }

  // -------------------------------------------------------
  // KPI cards — top-of-section quick summary per mode
  // -------------------------------------------------------

  private addKpiCards(results: AnalysisResults): void {
    const section = document.createElement('div');
    section.className = 'results-section fade-in';

    const cards: Array<{label: string; value: string; unit: string; cls: string; warn?: string}> = [];

    if (results.analysisMode === 'sphere-bestfit' || results.analysisMode === 'manual-geodesic' || results.analysisMode === 'two-sphere-auto') {
      // Linear wear
      if (results.zoneSpheres) {
        const lw = results.zoneSpheres.wornSphere.center.distanceTo(results.zoneSpheres.unwornSphere.center) * 1000;
        const unreliable = results.zoneSpheres.linearWearUnreliable;
        const lod = results.twoSphere && !results.twoSphere.detected ? results.twoSphere.detectionLimitUm : null;
        cards.push({
          label: 'Linear Wear',
          value: lod != null ? `< ${lod.toFixed(0)}` : lw.toFixed(1),
          unit: results.twoSphere?.linearWearSdMm != null ? `μm ± ${(results.twoSphere.linearWearSdMm * 1000).toFixed(1)}` : 'μm',
          cls: unreliable ? 'warning' : (lw > 50 ? 'danger' : lw > 20 ? 'warning' : 'success'),
          warn: unreliable
            ? (results.twoSphere && !results.twoSphere.detected ? 'below detection limit'
              : results.twoSphere?.nearPole ? '⚠ near pole' : '⚠ reliability issue')
            : undefined,
        });
        if (this.yearsInVivo > 0) {
          cards.push({
            label: 'Linear Rate',
            value: (lw / 1000 / this.yearsInVivo).toFixed(4),
            unit: 'mm / yr',
            cls: 'rate',
          });
        }
      }
      // Two-Sphere Auto without detected wear: the nominal-radius "volume" and the share of points
      // outside the nominal sphere only reflect the cavity being larger than the head (machining
      // clearance, paint), not wear — show them as not detected instead of as numbers.
      const tsNotDetected = results.analysisMode === 'two-sphere-auto' && !!results.twoSphere && !results.twoSphere.detected;
      if (tsNotDetected && results.wearVolumeResult) {
        cards.push({ label: 'Volumetric Wear', value: '—', unit: 'mm³', cls: 'success', warn: 'no wear detected' });
      }
      // Volumetric wear
      if (results.wearVolumeResult && !tsNotDetected) {
        const vw = results.wearVolumeResult.wearVolume;
        cards.push({
          label: 'Volumetric Wear',
          value: vw.toFixed(4),
          unit: results.twoSphere?.volumeSdMm3 != null ? `mm³ ± ${results.twoSphere.volumeSdMm3.toFixed(1)}` : 'mm³',
          cls: vw > 0.1 ? 'danger' : 'success',
        });
        if (this.yearsInVivo > 0) {
          cards.push({
            label: 'Volumetric Rate',
            value: (vw / this.yearsInVivo).toFixed(4),
            unit: 'mm³ / yr',
            cls: 'rate',
          });
        }
      }
      // Worn %: in Two-Sphere Auto the worn area is the part of the surface on the displaced sphere
      if (results.analysisMode === 'two-sphere-auto' && results.twoSphere) {
        const ts = results.twoSphere;
        const wp = ts.detected ? ts.wornAreaPct ?? null : null;
        cards.push({
          label: 'Worn Surface',
          value: wp != null ? wp.toFixed(1) : '—',
          unit: '%',
          cls: wp == null ? 'success' : wp > 10 ? 'danger' : wp > 2 ? 'warning' : 'success',
          warn: ts.detected ? undefined : 'no wear detected',
        });
      } else if (results.wearClassification) {
        const wp = results.wearClassification.wornPercent;
        cards.push({
          label: 'Worn Surface',
          value: wp.toFixed(1),
          unit: '%',
          cls: wp > 10 ? 'danger' : wp > 2 ? 'warning' : 'success',
        });
      }
      // Sphericity (one value per sample, shown next to the wear figures)
      if (results.sphericity) {
        const sp = results.sphericity.sphericityPercent;
        cards.push({
          label: 'Sphericity',
          value: sp.toFixed(2),
          unit: `% · P–V ${results.sphericity.sphericityUm.toFixed(0)} μm`,
          cls: sp >= 99.5 ? 'success' : sp >= 98 ? 'warning' : 'danger',
        });
      }

    } else if (results.analysisMode === 'double-sphere-metrics') {
      const best = results.doubleSphereMetrics?.bestCell;
      if (best) {
        const lw = best.centerDistanceMean * 1000;
        cards.push({
          label: 'Linear Wear',
          value: lw.toFixed(1),
          unit: 'μm',
          cls: lw > 50 ? 'danger' : lw > 20 ? 'warning' : 'success',
        });
        cards.push({
          label: 'Wear Std',
          value: (best.centerDistanceStd * 1000).toFixed(1),
          unit: 'μm',
          cls: 'neutral',
        });
        if (this.yearsInVivo > 0) {
          cards.push({
            label: 'Linear Rate',
            value: (best.centerDistanceMean / this.yearsInVivo).toFixed(4),
            unit: 'mm / yr',
            cls: 'rate',
          });
        }
      }
      if (results.wearVolumeResult) {
        const vw = results.wearVolumeResult.wearVolume;
        cards.push({
          label: 'Volumetric Wear',
          value: vw.toFixed(4),
          unit: 'mm³',
          cls: vw > 0.1 ? 'danger' : 'success',
        });
        if (this.yearsInVivo > 0) {
          cards.push({
            label: 'Volumetric Rate',
            value: (vw / this.yearsInVivo).toFixed(4),
            unit: 'mm³ / yr',
            cls: 'rate',
          });
        }
      }

    } else {
      // pure-geodesic
      const depth = results.wearVector?.maxDepth ?? results.primaryWearZone?.maxDeviation ?? null;
      if (depth !== null) {
        cards.push({
          label: 'Max Wear Depth',
          value: depth.toFixed(1),
          unit: 'μm',
          cls: depth > 50 ? 'danger' : depth > 20 ? 'warning' : 'success',
        });
      }
      const vw = results.totalBumpVolume;
      cards.push({
        label: 'Volumetric Wear',
        value: vw.toFixed(4),
        unit: 'mm³',
        cls: vw > 0.1 ? 'danger' : 'success',
      });
      if (this.yearsInVivo > 0) {
        cards.push({
          label: 'Volumetric Rate',
          value: (vw / this.yearsInVivo).toFixed(4),
          unit: 'mm³ / yr',
          cls: 'rate',
        });
      }
      cards.push({
        label: 'Wear Clusters',
        value: results.bumpClusters.length.toString(),
        unit: 'clusters',
        cls: results.bumpClusters.length > 0 ? 'danger' : 'success',
      });
    }

    if (cards.length === 0) {
      section.style.display = 'none';
      this.container.appendChild(section);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'kpi-grid';
    for (const c of cards) {
      const card = document.createElement('div');
      card.className = `kpi-card ${c.cls}`;
      card.innerHTML = `
        <div class="kpi-label">${c.label}</div>
        <div class="kpi-value">${c.value}</div>
        <div class="kpi-unit">${c.unit}</div>
        ${c.warn ? `<div class="kpi-warn-badge">${c.warn}</div>` : ''}
      `;
      grid.appendChild(card);
    }
    section.appendChild(grid);
    this.container.appendChild(section);
  }

  private addDoubleSphereSection(results: AnalysisResults): void {
    const section = this.createSection('Double Sphere Metrics');
    const ds = results.doubleSphereMetrics;
    if (!ds) {
      this.addMetric(section, 'Status', 'No sweep results available', undefined, 'warning');
      this.container.appendChild(section);
      return;
    }

    this.addMetric(section, 'Factor', ds.factor.toFixed(3));
    this.addMetric(section, 'Iterations', ds.iterations.toString());
    this.addMetric(section, 'Thresh1 Values', ds.thresh1Values.length.toString());
    this.addMetric(section, 'Thresh2 Values', ds.thresh2Values.length.toString());
    this.addMetric(section, 'Computed Cells', ds.cells.length.toString());
    if (ds.seed != null) this.addMetric(section, 'Bootstrap Seed', ds.seed.toString());
    if (ds.estimator) {
      this.addMetric(section, 'Cell Selection', ds.estimator === 'stable-quartile' ? 'Median of most stable 25%' : 'Min-dispersion cell (legacy)');
    }
    if (ds.cellDistanceMedian != null && ds.cellDistanceIQR) {
      this.addMetric(section, 'Sweep Median (IQR)',
        `${(ds.cellDistanceMedian * 1000).toFixed(1)} (${(ds.cellDistanceIQR[0] * 1000).toFixed(1)}–${(ds.cellDistanceIQR[1] * 1000).toFixed(1)})`, 'μm');
    }
    if (ds.bestCell) {
      this.addMetric(section, 'Selected Thresh1', ds.bestCell.thresh1.toFixed(3));
      this.addMetric(section, 'Selected Thresh2', ds.bestCell.thresh2.toFixed(3));
      this.addMetric(section, 'Best Center Dist Mean', ds.bestCell.centerDistanceMean.toFixed(4), 'mm', 'warning', true);
      this.addMetric(section, 'Best Center Dist Std', ds.bestCell.centerDistanceStd.toFixed(4), 'mm');
      if (this.yearsInVivo > 0) {
        const linearRate = ds.bestCell.centerDistanceMean / this.yearsInVivo;
        this.addRateMetric(section, 'Linear Wear Rate', linearRate.toFixed(4), 'mm/year',
          linearRate > 0.01 ? 'danger' : 'success');
      }
      this.addMetric(section, 'Best Radius1 Mean', ds.bestCell.radius1Mean.toFixed(4), 'mm');
      this.addMetric(section, 'Best Radius2 Mean', ds.bestCell.radius2Mean.toFixed(4), 'mm');
    }

    this.container.appendChild(section);

    const table = this.createSection('Double Sphere Sweep Table');
    for (const cell of ds.cells) {
      this.addMetric(
        table,
        `t1=${cell.thresh1.toFixed(3)} | t2=${cell.thresh2.toFixed(3)}`,
        `${(cell.centerDistanceMean * 1000).toFixed(1)} ± ${(cell.centerDistanceStd * 1000).toFixed(1)} μm`
      );
    }
    this.container.appendChild(table);
  }

  private addSphereFitSection(results: AnalysisResults): void {
    const section = this.createSection('Sphere Fit');
    const sf = results.sphereFit;

    this.addMetric(section, 'Radius', sf.radius.toFixed(4), 'mm');
    this.addMetric(section, 'Center X', sf.center.x.toFixed(4), 'mm');
    this.addMetric(section, 'Center Y', sf.center.y.toFixed(4), 'mm');
    this.addMetric(section, 'Center Z', sf.center.z.toFixed(4), 'mm');
    this.addMetric(section, 'RMS Error', (sf.rmsError * 1000).toFixed(2), 'μm');
    this.addMetric(section, 'Max Error', (sf.maxError * 1000).toFixed(2), 'μm');

    this.container.appendChild(section);
  }

  private addShapeSection(results: AnalysisResults): void {
    const section = this.createSection('Shape Classification');
    const ef = results.ellipsoidFit;
    if (!ef) {
      this.addMetric(section, 'Sphericity', 'N/A');
      this.addMetric(section, 'Classification', 'N/A');
      this.addMetric(section, 'Semi-axis A', 'N/A');
      this.addMetric(section, 'Semi-axis B', 'N/A');
      this.addMetric(section, 'Semi-axis C', 'N/A');
      this.addMetric(section, 'Axis ratio (B/A)', 'N/A');
      this.addMetric(section, 'Axis ratio (C/A)', 'N/A');
    } else {
      this.addMetric(section, 'Sphericity', `${ef.sphericityPercent.toFixed(2)}`, '%',
        ef.sphericityPercent >= 98 ? 'success' : ef.sphericityPercent >= 90 ? 'warning' : 'danger');
      this.addMetric(section, 'Classification', ef.shapeClass.replace(/-/g, ' '));
      this.addMetric(section, 'Semi-axis A', ef.semiAxes[0].toFixed(4), 'mm');
      this.addMetric(section, 'Semi-axis B', ef.semiAxes[1].toFixed(4), 'mm');
      this.addMetric(section, 'Semi-axis C', ef.semiAxes[2].toFixed(4), 'mm');
      this.addMetric(section, 'Axis ratio (B/A)', (ef.semiAxes[1] / ef.semiAxes[0]).toFixed(4));
      this.addMetric(section, 'Axis ratio (C/A)', (ef.semiAxes[2] / ef.semiAxes[0]).toFixed(4));
    }
    this.container.appendChild(section);
  }

  private addWearSection(results: AnalysisResults): void {
    const section = this.createSection('Wear Analysis');

    // Find max bump and max dip across all clusters
    let maxBump = 0, maxDip = 0;
    for (const c of results.bumpClusters) {
      if (c.maxDeviation > maxBump) maxBump = c.maxDeviation;
    }
    for (const c of results.dipClusters) {
      if (c.minDeviation < maxDip) maxDip = c.minDeviation;
    }

    this.addMetric(section, 'Max Wear', `+${maxBump.toFixed(2)}`, 'μm',
      maxBump > 10 ? 'danger' : maxBump > 5 ? 'warning' : 'success');

    if (results.primaryWearZone) {
      const pwz = results.primaryWearZone;
      this.addMetric(section, 'Primary Wear Depth', `+${pwz.maxDeviation.toFixed(2)}`, 'μm', 'danger');
      this.addMetric(section, 'Primary Wear Area', pwz.area.toFixed(2), 'mm²');
    }

    this.container.appendChild(section);
  }

  private addVolumeSection(results: AnalysisResults): void {
    const section = this.createSection('Volume Analysis');

    this.addMetric(section, 'Wear Volume',
      results.totalBumpVolume.toFixed(4), 'mm³',
      results.totalBumpVolume > 0.1 ? 'danger' : 'success');

    // Mass estimates
    const density = 0.935; // g/cm³ = mg/mm³
    this.addMetric(section, 'Wear Mass',
      (results.totalBumpVolume * density).toFixed(4), 'mg', 'danger');

    this.container.appendChild(section);
  }

  private addWearVectorSection(results: AnalysisResults): void {
    const section = this.createSection('Wear Vector');
    const wv = results.wearVector!;

    this.addMetric(section, 'Max Depth', wv.maxDepth.toFixed(2), 'μm', 'danger');
    this.addMetric(section, 'Distance to Pole', wv.distance.toFixed(3), 'mm');
    this.addMetric(section, 'Angle from Axis', wv.angle.toFixed(1), '°');
    this.addMetric(section, 'Deepest Pt X', wv.deepestPoint.x.toFixed(3), 'mm');
    this.addMetric(section, 'Deepest Pt Y', wv.deepestPoint.y.toFixed(3), 'mm');
    this.addMetric(section, 'Deepest Pt Z', wv.deepestPoint.z.toFixed(3), 'mm');

    this.container.appendChild(section);
  }

  private addCommercialSphereSection(results: AnalysisResults): void {
    const section = this.createSection('Commercial Sphere');
    const cs = results.commercialSphere!;

    this.addMetric(section, 'Geodesic Radius', cs.geodesicRadius.toFixed(4), 'mm');
    this.addMetric(section, 'Commercial Radius', cs.commercialRadius.toFixed(1), 'mm');
    this.addMetric(section, 'Detection',
      cs.autoDetected
        ? (cs.commercialRadius > cs.geodesicRadius ? 'Auto (snap up)' : 'Auto (round down)')
        : 'Manual');
    this.addMetric(section, 'Center X', cs.center.x.toFixed(4), 'mm');
    this.addMetric(section, 'Center Y', cs.center.y.toFixed(4), 'mm');
    this.addMetric(section, 'Center Z', cs.center.z.toFixed(4), 'mm');

    this.container.appendChild(section);
  }

  private addWearClassificationSection(results: AnalysisResults): void {
    const section = this.createSection('Wear Classification');
    const wc = results.wearClassification!;

    this.addMetric(section, 'Threshold', wc.threshold.toFixed(3), 'mm');
    if (wc.thresholdMode) {
      this.addMetric(section, 'Threshold Rule', wc.thresholdMode === 'noise-adaptive' ? 'Noise-adaptive (k·σ)' : 'Legacy 1.02·R');
    }
    if (wc.thresholdMode === 'noise-adaptive') {
      this.addMetric(section, 'Scan Noise σ (MAD)', (wc.noiseSigmaUm ?? 0).toFixed(1), 'μm');
      this.addMetric(section, 'Reference Baseline', (wc.baselineUm ?? 0).toFixed(1), 'μm');
      this.addMetric(section, 'Threshold over R', (wc.depthThresholdUm ?? 0).toFixed(1), 'μm');
      if (wc.noiseAboveLegacy) {
        this.addMetric(section, 'Warning', 'Surface deviates from the sphere more than noise — threshold capped at 2 %', undefined, 'warning');
      }
    }
    this.addMetric(section, 'Worn Vertices', wc.wornCount.toLocaleString(), undefined,
      wc.wornCount > 0 ? 'danger' : 'success');
    this.addMetric(section, 'Unworn Vertices', wc.unwornCount.toLocaleString(), undefined, 'success');
    this.addMetric(section, 'Worn %', wc.wornPercent.toFixed(2), '%',
      wc.wornPercent > 10 ? 'danger' : wc.wornPercent > 2 ? 'warning' : 'success');

    this.container.appendChild(section);
  }

  private addTwoSphereSection(results: AnalysisResults): void {
    const section = this.createSection('Two-Sphere Fit');
    const ts = results.twoSphere!;
    this.addMetric(section, 'Status',
      ts.detected ? 'Directional wear detected' : 'No directional wear detected',
      undefined, ts.detected ? 'success' : 'warning');
    if (ts.detected) {
      this.addMetric(section, 'Linear Wear', (ts.linearWearMm * 1000).toFixed(1), 'μm', 'danger', true);
    } else if (ts.detectionLimitUm != null) {
      this.addMetric(section, 'Linear Wear', `< ${ts.detectionLimitUm.toFixed(0)} (below the detection limit)`, 'μm', 'warning', true);
    } else {
      this.addMetric(section, 'Linear Wear', '0.0', 'μm', 'warning', true);
    }
    if (ts.detectionLimitUm != null) {
      this.addMetric(section, 'Detection limit for this surface (penetration ≥ 30° from the axis; wear near the pole may stay undetected)', ts.detectionLimitUm.toFixed(0), 'μm');
    }
    if (ts.linearCorrectedMm != null) {
      this.addMetric(section, 'Linear Wear corrected for cavity-radius excess',
        `${(ts.linearCorrectedMm * 1000).toFixed(1)}${ts.linearCorrectedSdMm != null ? ` ± ${(ts.linearCorrectedSdMm * 1000).toFixed(1)}` : ''}`, 'μm');
    }
    if (ts.wornAreaMm2 != null) {
      this.addMetric(section, 'Worn area (displaced-sphere surface)',
        `${ts.wornAreaMm2.toFixed(0)}${ts.wornAreaPct != null ? ` (${ts.wornAreaPct.toFixed(1)} % of the analysed surface)` : ''}`, 'mm²');
    }
    if (ts.linearWearSdMm != null) {
      this.addMetric(section, 'Linear Wear uncertainty (SD)', (ts.linearWearSdMm * 1000).toFixed(1), 'μm');
    }
    if (ts.volumeSdMm3 != null) {
      this.addMetric(section, 'Volumetric Wear uncertainty (SD)', ts.volumeSdMm3.toFixed(1), 'mm³');
    }
    if (ts.linearSdBootstrapMm != null && ts.linearSdPlaneMm != null) {
      this.addMetric(section, 'SD components · bootstrap / cut plane ±1 % / surface form',
        `${(ts.linearSdBootstrapMm * 1000).toFixed(1)} / ${(ts.linearSdPlaneMm * 1000).toFixed(1)} / ${((ts.linearSdSystematicMm ?? 0) * 1000).toFixed(1)} μm · ` +
        `${(ts.volumeSdBootstrapMm3 ?? 0).toFixed(1)} / ${(ts.volumeSdPlaneMm3 ?? 0).toFixed(1)} / ${(ts.volumeSdSystematicMm3 ?? 0).toFixed(1)} mm³`);
    }
    if (ts.linearSdAcquisitionMm != null) {
      this.addMetric(section, 'SD component · acquisition (paint + other, entered)',
        `${(ts.linearSdAcquisitionMm * 1000).toFixed(1)} μm · ${(ts.volumeSdAcquisitionMm3 ?? 0).toFixed(1)} mm³ ` +
        `(surface u = ${(ts.surfaceUncertaintyUm ?? 0).toFixed(1)} μm, worn area ${(ts.wornAreaMm2 ?? 0).toFixed(0)} mm²)`);
    }
    if (ts.lowFreqRmsUm != null) {
      this.addMetric(section, 'Surface form error (low-frequency RMS)', ts.lowFreqRmsUm.toFixed(1), 'μm');
    }
    if (ts.alternativeVolumeMm3 != null) {
      this.addMetric(section, ts.inverted ? 'Volume if Auto direction' : 'Volume if Inverted direction',
        ts.alternativeVolumeMm3.toFixed(2), 'mm³');
    }
    if (ts.outerShell) {
      const o = ts.outerShell;
      const verdict = o.favours === 'current' ? 'supports the current choice'
        : o.favours === 'alternative' ? 'supports the OPPOSITE direction' : 'undetermined';
      this.addMetric(section, 'Outer-shell concentricity',
        `${verdict} (outer centre ${o.distToOriginalMm.toFixed(2)} mm from original / ${o.distToDisplacedMm.toFixed(2)} mm from displaced; outer RMS ${o.rmsUm.toFixed(0)} μm)`,
        undefined, o.favours === 'alternative' ? 'warning' : undefined);
    }
    if (ts.radiusSelection && ts.radiusSelection.chosen !== ts.radiusSelection.snapped) {
      this.addMetric(section, 'Radius selection',
        `${ts.radiusSelection.chosen} mm chosen by the two-sphere fit (general fit suggested ${ts.radiusSelection.snapped} mm)`, undefined, 'warning');
    }
    this.addMetric(section, 'Original sphere', ts.inverted
      ? 'Inverted by user (head displaced toward the rim)'
      : 'Auto (head displaced into the cup)', undefined, ts.inverted ? 'warning' : undefined);
    if (ts.directionAngleDeg !== null) {
      this.addMetric(section, 'Direction to cup axis', ts.directionAngleDeg.toFixed(1), '°', ts.nearPole ? 'warning' : undefined);
    }
    if (ts.nearPole) {
      this.addMetric(section, 'Warning', 'Penetration < 30° from the cup axis: small unworn reference, reduced accuracy', undefined, 'warning');
    }
    if (!ts.detected) {
      this.addMetric(section, 'Note', 'Below the detection limit, or a uniformly enlarged cavity (no two-sphere pattern)', undefined, 'warning');
    }
    this.addMetric(section, 'Sphere radius (both)', ts.radius.toFixed(3), 'mm');
    this.addMetric(section, 'Scanner noise σ', ts.noiseSigmaUm.toFixed(1), 'μm');
    this.addMetric(section, 'Unworn surface', (ts.unwornFraction * 100).toFixed(1), '%');
    this.addMetric(section, 'Reference vertices', `${ts.referenceVertexCount.toLocaleString()} / ${ts.activeVertexCount.toLocaleString()}`);
    this.addMetric(section, 'RMS residual · 1 sphere', Math.sqrt(ts.msOneSphereUm2).toFixed(1), 'μm');
    this.addMetric(section, 'RMS residual · 2 spheres', Math.sqrt(ts.msTwoSpheresUm2).toFixed(1), 'μm');
    this.addMetric(section, 'RMS residual · free radius', `${Math.sqrt(ts.msFreeSphereUm2).toFixed(1)} (R = ${ts.freeSphereRadius.toFixed(3)} mm)`, 'μm');
    this.container.appendChild(section);
  }

  private addZoneSpheresSection(results: AnalysisResults): void {
    const section = this.createSection('Zone Spheres');
    const zs = results.zoneSpheres!;

    const linearWear = zs.wornSphere.center.distanceTo(zs.unwornSphere.center);
    const unreliable = zs.linearWearUnreliable;
    this.addMetric(section, unreliable ? 'Linear Wear ⚠' : 'Linear Wear',
      (linearWear * 1000).toFixed(1), 'μm', unreliable ? 'warning' : 'danger');

    if (unreliable && zs.unreliableReason) {
      this.addMetric(section, 'Warning', zs.unreliableReason, undefined, 'warning');
    }

    if (zs.filterUsed) {
      const filterNames: Record<string, string> = {
        'none': 'None',
        'robust-irls': 'Robust IRLS',
        'dbscan-spatial': 'Spatial DBSCAN',
        'combined': 'Combined',
      };
      this.addMetric(section, 'Filter', filterNames[zs.filterUsed] || zs.filterUsed);
    }
    if (zs.rawWornVertexCount !== undefined && zs.filteredWornVertexCount !== undefined) {
      this.addMetric(section, 'Worn Vertices (raw)', zs.rawWornVertexCount.toLocaleString());
      this.addMetric(section, 'Worn Vertices (filtered)', zs.filteredWornVertexCount.toLocaleString(),
        undefined, zs.filteredWornVertexCount < zs.rawWornVertexCount ? 'warning' : 'success');
    }
    if (zs.discardedClusters !== undefined && zs.discardedClusters > 0) {
      this.addMetric(section, 'Discarded Clusters', zs.discardedClusters.toString(), undefined, 'warning');
    }

    this.addMetric(section, 'Worn Sphere RMS', (zs.wornSphere.rmsError * 1000).toFixed(2), 'μm', 'danger');
    this.addMetric(section, 'Unworn Sphere RMS', (zs.unwornSphere.rmsError * 1000).toFixed(2), 'μm', 'success');

    this.container.appendChild(section);
  }

  private addWearVolumeSection(results: AnalysisResults): void {
    const section = this.createSection('Wear Volume');
    const wv = results.wearVolumeResult!;
    const density = 0.935; // UHMWPE density g/cm³ = mg/mm³

    this.addMetric(section, 'Mesh Enclosed Vol', wv.meshEnclosedVolume.toFixed(4), 'mm³');
    this.addMetric(section, 'Sphere Cap Vol', wv.sphereCapVolume.toFixed(4), 'mm³');
    this.addMetric(section, 'Wear Volume', wv.wearVolume.toFixed(4), 'mm³',
      wv.wearVolume > 0.1 ? 'danger' : 'success');
    this.addMetric(section, 'Wear Mass', (wv.wearVolume * density).toFixed(4), 'mg',
      wv.wearVolume > 0.1 ? 'danger' : 'success');

    if (wv.measuredRadius) {
      const m = wv.measuredRadius;
      this.addMetric(section, 'Wear Volume · measured radius',
        `${m.wearVolume.toFixed(2)}${m.wearVolumeSdMm3 != null ? ` ± ${m.wearVolumeSdMm3.toFixed(1)}` : ''}`, 'mm³', m.reliable ? undefined : 'warning');
      this.addMetric(section, 'Measured cavity radius', `${m.radius.toFixed(3)} (nominal ${(results.commercialSphere?.commercialRadius ?? 0).toFixed(1)})`, 'mm');
      this.addMetric(section, 'Note', m.reliable
        ? 'Measured-radius volume excludes uniform enlargement (design clearance, machining, creep) and any uniformly distributed wear'
        : 'Measured radius unreliable (small non-worn reference or radius > 0.5 mm from nominal)', undefined, m.reliable ? undefined : 'warning');
    }

    if (wv.unfilledHoles && wv.unfilledHoles.count > 0) {
      const h = wv.unfilledHoles;
      const serious = h.missingVolumeMm3 > 2;
      this.addMetric(section, 'Open holes in the inner surface',
        `${h.count} hole(s) · ≈ ${h.missingVolumeMm3.toFixed(1)} mm³ missing from the volume` +
        (serious ? ' — enable "Repair Inner Face" or mark the large holes with a seed and re-run' : ' (negligible)'),
        undefined, serious ? 'danger' : undefined);
    }

    if (this.yearsInVivo > 0) {
      const volRate = wv.wearVolume / this.yearsInVivo;
      this.addRateMetric(section, 'Volumetric Wear Rate', volRate.toFixed(4), 'mm³/year',
        volRate > 0.01 ? 'danger' : 'success');
      this.addRateMetric(section, 'Mass Wear Rate', (volRate * density).toFixed(4), 'mg/year');
    }

    this.container.appendChild(section);
  }

  private addWearPlaneSection(results: AnalysisResults): void {
    const section = this.createSection('Maximum Wear Point');
    const wp = results.wearPlane!;

    this.addMetric(section, 'Max Wear Depth', wp.maxWearDepth.toFixed(1), 'μm',
      wp.maxWearDepth > 10 ? 'danger' : wp.maxWearDepth > 5 ? 'warning' : 'success');
    this.addMetric(section, 'Point X', wp.maxWearPoint.x.toFixed(4), 'mm');
    this.addMetric(section, 'Point Y', wp.maxWearPoint.y.toFixed(4), 'mm');
    this.addMetric(section, 'Point Z', wp.maxWearPoint.z.toFixed(4), 'mm');

    this.container.appendChild(section);
  }

  private addGeodesicTable(geodesics: Geodesic[]): void {
    const section = this.createSection('Geodesic Details');

    const tableWrapper = document.createElement('div');
    tableWrapper.style.maxHeight = '300px';
    tableWrapper.style.overflowY = 'auto';

    const table = document.createElement('table');
    table.className = 'geodesic-table';

    // Header
    const thead = document.createElement('thead');
    thead.innerHTML = `
      <tr>
        <th>Angle</th>
        <th>Max</th>
        <th>Min</th>
        <th>Anom.</th>
      </tr>`;
    table.appendChild(thead);

    // Body
    const tbody = document.createElement('tbody');
    for (const geo of geodesics) {
      const tr = document.createElement('tr');
      if (geo.anomalyCount > 0) tr.className = 'anomaly';

      tr.innerHTML = `
        <td>${geo.angle.toFixed(0)}°</td>
        <td style="color: ${geo.maxDeviation > 1 ? '#f85149' : 'inherit'}">${geo.maxDeviation.toFixed(1)}</td>
        <td style="color: ${geo.minDeviation < -1 ? '#6644ff' : 'inherit'}">${geo.minDeviation.toFixed(1)}</td>
        <td>${geo.anomalyCount}</td>
      `;

      tr.addEventListener('click', () => {
        if (this.onGeodesicSelect) {
          this.onGeodesicSelect(geo.angle);
        }
      });

      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    tableWrapper.appendChild(table);
    section.appendChild(tableWrapper);

    this.container.appendChild(section);
  }

  private createSection(title: string): HTMLElement {
    const section = document.createElement('div');
    section.className = 'results-section fade-in';

    // Add small icon prefix based on section title for scannability
    const icons: Record<string, string> = {
      'Summary': '📋',
      'Sphere Fit': '⭕',
      'Shape Classification': '📐',
      'Wear Analysis': '🔬',
      'Volume Analysis': '📦',
      'Wear Vector': '📍',
      'Commercial Sphere': '🏭',
      'Wear Classification': '🗂️',
      'Zone Spheres': '🎯',
      'Wear Volume': '📊',
      'Maximum Wear Point': '📌',
      'Double Sphere Metrics': '🔄',
      'Two-Sphere Fit': '⚪',
      'Sphericity': '🔵',
      'Double Sphere Sweep Table': '🗃️',
      'Geodesic Details': '🌐',
    };
    const icon = Object.entries(icons).find(([k]) => title.startsWith(k))?.[1] ?? '';

    const h3 = document.createElement('h3');
    h3.textContent = icon ? `${icon} ${title}` : title;
    section.appendChild(h3);

    return section;
  }

  private addMetric(
    section: HTMLElement,
    label: string,
    value: string,
    unit?: string,
    colorClass?: string,
    highlight?: boolean
  ): void {
    const row = document.createElement('div');
    row.className = highlight ? 'metric-row highlight' : 'metric-row';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'metric-label';
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.className = `metric-value ${colorClass || ''}`;
    valueSpan.textContent = value;
    if (unit) {
      const unitSpan = document.createElement('span');
      unitSpan.className = 'metric-unit';
      unitSpan.textContent = unit;
      valueSpan.appendChild(unitSpan);
    }

    row.appendChild(labelSpan);
    row.appendChild(valueSpan);
    section.appendChild(row);
  }

  /** Like addMetric but styled with blue accent for wear-rate rows. */
  private addRateMetric(
    section: HTMLElement,
    label: string,
    value: string,
    unit?: string,
    colorClass?: string
  ): void {
    const row = document.createElement('div');
    row.className = 'metric-row rate-highlight';

    const labelSpan = document.createElement('span');
    labelSpan.className = 'metric-label';
    labelSpan.textContent = label;

    const valueSpan = document.createElement('span');
    valueSpan.className = `metric-value ${colorClass || ''}`;
    valueSpan.textContent = value;
    if (unit) {
      const unitSpan = document.createElement('span');
      unitSpan.className = 'metric-unit';
      unitSpan.textContent = unit;
      valueSpan.appendChild(unitSpan);
    }

    row.appendChild(labelSpan);
    row.appendChild(valueSpan);
    section.appendChild(row);
  }
}
